import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Card,
  IconButton,
  Paper,
  Menu,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Building2,
  Share2,
  Download,
  FolderOpen,
  Save,
  Sparkles,
  MapPin,
  ChevronDown,
  Plus,
  X,
  BarChart3,
  PieChart,
  TrendingUp,
  DollarSign,
  Users,
  AlertTriangle,
  Home,
  Eye,
  EyeOff,
} from 'lucide-react';
import Header from '../../layout/Header';
import { loadModules } from 'esri-loader';
import GeometryTools from './components/GeometryTools';
import DataCard from './components/DataCard';
import PresetsMenu from './components/PresetsMenu';
import PropertyFiltersDialog from './components/PropertyFiltersDialog';
import ExportShareMenu from './components/ExportShareMenu';
import TemplatesManager from './components/TemplatesManager';
import { useAuth } from '../../../AuthProvider';
import { useNavigate } from 'react-router-dom';

const DISCOVER_PERSIST_KEY = 'vestmap_discover_persisted_state';

function loadPersistedDiscoverState() {
  try {
    const raw = sessionStorage.getItem(DISCOVER_PERSIST_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function savePersistedDiscoverState(state) {
  try {
    if (state) {
      sessionStorage.setItem(DISCOVER_PERSIST_KEY, JSON.stringify(state));
    }
  } catch (e) {
    console.warn('[Discover] Could not persist state', e);
  }
}

const DEFAULT_TEMPLATE_ID = '671fbaded8a7c1565b9b7d85';

const DEFAULT_CARDS = [
  { id: 'rent-by-bed', visible: true, pinned: false },
  { id: 'rent-vs-sqft', visible: true, pinned: false },
  { id: `${DEFAULT_TEMPLATE_ID}-demographics-map`, visible: true, pinned: false },
  { id: `${DEFAULT_TEMPLATE_ID}-demographics-data`, visible: true, pinned: false },
  { id: `${DEFAULT_TEMPLATE_ID}-expansion-map`, visible: true, pinned: false },
  { id: `${DEFAULT_TEMPLATE_ID}-expansion-data`, visible: true, pinned: false },
  { id: 'comps-table', visible: true, pinned: false },
];

const STATIC_CARDS = [
  {
    id: 'rent-by-bed',
    name: 'Rent Overview by Bed',
    description: 'Median rent by bedroom count with benchmark comparisons',
    category: 'rents',
    icon: BarChart3,
    preview: 'Bar chart showing rent distribution across unit types',
  },
  {
    id: 'rent-vs-sqft',
    name: 'Rent vs Square Footage',
    description: 'Scatter plot analysis of rent per square foot',
    category: 'rents',
    icon: TrendingUp,
    preview: 'Scatter plot colored by bed count',
  },
  {
    id: 'comps-table',
    name: 'Recent Listings / Comps',
    description: 'Detailed comparable properties table',
    category: 'tables',
    icon: Home,
    preview: 'Sortable table with key metrics',
  },
];

const SECTION_KEY_ICONS = {
  demographics: { map: MapPin, data: Users },
  income: { map: DollarSign, data: DollarSign },
  expansion: { map: TrendingUp, data: TrendingUp },
  crime: { map: AlertTriangle, data: AlertTriangle },
};
const DEFAULT_SECTION_ICON = { map: MapPin, data: BarChart3 };

const CATEGORIES = [
  { id: 'all', name: 'All Cards' },
  { id: 'rents', name: 'Rents' },
  { id: 'supply', name: 'Supply' },
  { id: 'demand', name: 'Demand' },
  { id: 'risk', name: 'Risk' },
  { id: 'quality', name: 'Quality' },
  { id: 'tables', name: 'Tables' },
];

const Discover = ({
  sharedMode = false,
  sharedState = null,
  allowDownload = false,
  onExportPdf = null,
  pdfGenerating = false,
}) => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const persistedState = useMemo(
    () => (sharedMode ? null : loadPersistedDiscoverState()),
    [sharedMode],
  );

  const [searchQuery, setSearchQuery] = useState(() => {
    if (persistedState?.searchQuery != null) return persistedState.searchQuery;
    if (sharedMode && sharedState?.searchQuery) return sharedState.searchQuery;
    return '';
  });
  const [rentPsfMin, setRentPsfMin] = useState(() => {
    if (persistedState?.filters?.rentPsfMin != null) return persistedState.filters.rentPsfMin;
    if (sharedMode && sharedState?.filters?.rentPsfMin != null) return sharedState.filters.rentPsfMin;
    return '';
  });
  const [rentPsfMax, setRentPsfMax] = useState(() => {
    if (persistedState?.filters?.rentPsfMax != null) return persistedState.filters.rentPsfMax;
    if (sharedMode && sharedState?.filters?.rentPsfMax != null) return sharedState.filters.rentPsfMax;
    return '';
  });
  const [rentBasis, setRentBasis] = useState(() => {
    if (persistedState?.filters?.rentBasis != null) return persistedState.filters.rentBasis;
    if (sharedMode && sharedState?.filters?.rentBasis) return sharedState.filters.rentBasis;
    return 'asking';
  });
  const [timeWindow, setTimeWindow] = useState(() => {
    if (persistedState?.filters?.timeWindow != null) return persistedState.filters.timeWindow;
    if (sharedMode && sharedState?.filters?.timeWindow) return sharedState.filters.timeWindow;
    return '90';
  });
  const [unitType, setUnitType] = useState(() => {
    if (persistedState?.filters?.unitType != null) return persistedState.filters.unitType;
    if (sharedMode && sharedState?.filters?.unitType) return sharedState.filters.unitType;
    return 'all-beds';
  });
  const [bathroomType, setBathroomType] = useState(() => {
    if (persistedState?.filters?.bathroomType != null) return persistedState.filters.bathroomType;
    if (sharedMode && sharedState?.filters?.bathroomType) return sharedState.filters.bathroomType;
    return 'all-baths';
  });
  const [geometries, setGeometries] = useState(() => {
    if (persistedState?.filters?.geometries != null) return persistedState.filters.geometries;
    if (sharedMode && sharedState?.filters?.geometries) return sharedState.filters.geometries;
    return [];
  });
  const [searchRadius, setSearchRadius] = useState(() => {
    const fromPersisted = persistedState?.searchRadius ?? persistedState?.filters?.searchRadius;
    const fromShared = sharedMode && sharedState?.searchRadius;
    const initialRadius = fromPersisted ?? fromShared ?? 5;
    const numRadius = typeof initialRadius === 'string' ? parseFloat(initialRadius) : initialRadius;
    return (typeof numRadius === 'number' && !isNaN(numRadius) && numRadius > 0) ? numRadius : 5;
  });
  const [originalSearchRadius, setOriginalSearchRadius] = useState(null);
  const [originalSearchResults, setOriginalSearchResults] = useState(null);
  const [useExtentFiltering, setUseExtentFiltering] = useState(false);
  const [extentFilteredBuildings, setExtentFilteredBuildings] = useState(null);
  const [radiusFilterActive, setRadiusFilterActive] = useState(true);
  const [showCardLibrary, setShowCardLibrary] = useState(false);
  const [cardLibrarySearch, setCardLibrarySearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeCards, setActiveCards] = useState(() => {
    if (persistedState?.layout?.activeCards?.length) return persistedState.layout.activeCards;
    if (sharedMode && sharedState?.layout?.activeCards?.length) return sharedState.layout.activeCards;
    return DEFAULT_CARDS;
  });
  const [draggedCard, setDraggedCard] = useState(null);
  const [showTemplatesManager, setShowTemplatesManager] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveDialogType, setSaveDialogType] = useState('template');
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [activePresets, setActivePresets] = useState(() => {
    if (persistedState?.filters?.activePresets != null) return persistedState.filters.activePresets;
    if (sharedMode && sharedState?.filters?.activePresets) return sharedState.filters.activePresets;
    return [];
  });
  const [showPresetsMenu, setShowPresetsMenu] = useState(false);
  const [showPropertyFilters, setShowPropertyFilters] = useState(false);
  const [propertyFiltersActive, setPropertyFiltersActive] = useState(() => {
    if (persistedState?.filters?.appliedPropertyFilters != null) return !!persistedState.filters.appliedPropertyFilters;
    if (sharedMode && sharedState?.filters?.appliedPropertyFilters) return true;
    return false;
  });
  const [appliedPropertyFilters, setAppliedPropertyFilters] = useState(() => {
    if (persistedState?.filters?.appliedPropertyFilters != null) return persistedState.filters.appliedPropertyFilters;
    if (sharedMode && sharedState?.filters?.appliedPropertyFilters) return sharedState.filters.appliedPropertyFilters;
    return null;
  });
  const [showExportShare, setShowExportShare] = useState(false);
  const [saveMenuAnchor, setSaveMenuAnchor] = useState(null);
  const [sideBarState, setSideBarState] = useState(false);
  const [showMapOnMobile, setShowMapOnMobile] = useState(false);
  const [searchResults, setSearchResults] = useState(() => {
    if (persistedState?.searchResults != null) return persistedState.searchResults;
    if (sharedMode && sharedState?.searchResults) return sharedState.searchResults;
    return null;
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [demographicsData, setDemographicsData] = useState(null);
  const [demographicsWebmapID, setDemographicsWebmapID] = useState(() => {
    if (sharedMode && sharedState?.webmapIDs?.demographics != null) {
      return sharedState.webmapIDs.demographics;
    }
    if (persistedState?.webmapIDs?.demographics != null) {
      return persistedState.webmapIDs.demographics;
    }
    return null;
  });
  const [arcgisToken, setArcgisToken] = useState(null);
  const [demographicsAddressInfo, setDemographicsAddressInfo] = useState(() => {
    if (sharedMode && sharedState?.addressInfo?.demographics != null) {
      return sharedState.addressInfo.demographics;
    }
    if (persistedState?.addressInfo?.demographics != null) {
      return persistedState.addressInfo.demographics;
    }
    return null;
  });
  const [incomeData, setIncomeData] = useState(null);
  const [incomeWebmapID, setIncomeWebmapID] = useState(() => {
    if (sharedMode && sharedState?.webmapIDs?.income != null) {
      return sharedState.webmapIDs.income;
    }
    if (persistedState?.webmapIDs?.income != null) {
      return persistedState.webmapIDs.income;
    }
    return null;
  });
  const [incomeAddressInfo, setIncomeAddressInfo] = useState(() => {
    if (sharedMode && sharedState?.addressInfo?.income != null) {
      return sharedState.addressInfo.income;
    }
    if (persistedState?.addressInfo?.income != null) {
      return persistedState.addressInfo.income;
    }
    return null;
  });
  const [expansionData, setExpansionData] = useState(null);
  const [expansionWebmapID, setExpansionWebmapID] = useState(() => {
    if (sharedMode && sharedState?.webmapIDs?.expansion != null) {
      return sharedState.webmapIDs.expansion;
    }
    if (persistedState?.webmapIDs?.expansion != null) {
      return persistedState.webmapIDs.expansion;
    }
    return null;
  });
  const [expansionAddressInfo, setExpansionAddressInfo] = useState(() => {
    if (sharedMode && sharedState?.addressInfo?.expansion != null) {
      return sharedState.addressInfo.expansion;
    }
    if (persistedState?.addressInfo?.expansion != null) {
      return persistedState.addressInfo.expansion;
    }
    return null;
  });
  const [expansionSectionLoading, setExpansionSectionLoading] = useState(false);
  const [crimeData, setCrimeData] = useState(null);
  const [crimeWebmapID, setCrimeWebmapID] = useState(() => {
    if (sharedMode && sharedState?.webmapIDs?.crime != null) {
      return sharedState.webmapIDs.crime;
    }
    if (persistedState?.webmapIDs?.crime != null) {
      return persistedState.webmapIDs.crime;
    }
    return null;
  });
  const [crimeAddressInfo, setCrimeAddressInfo] = useState(() => {
    if (sharedMode && sharedState?.addressInfo?.crime != null) {
      return sharedState.addressInfo.crime;
    }
    if (persistedState?.addressInfo?.crime != null) {
      return persistedState.addressInfo.crime;
    }
    return null;
  });
  const [templatesWithSections, setTemplatesWithSections] = useState([]);
  const [sectionDataState, setSectionDataState] = useState(() => {
    if (sharedMode && sharedState?.sectionDataState != null) return sharedState.sectionDataState;
    if (persistedState?.sectionDataState != null) return persistedState.sectionDataState;
    return {};
  });
  const mapRef = useRef(null);
  const mapViewRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [autocompleteInitialized, setAutocompleteInitialized] = useState(false);
  const initializationTimeoutRef = useRef();
  const getDiscoverStateRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const url = process.env.REACT_APP_NODE_URL + '/templates/chat/with-sections';
    const fetchTemplates = async () => {
      try {
        const token = await getAccessToken();
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) setTemplatesWithSections(data);
      } catch (err) {
        console.error('[Discover] Failed to fetch templates with sections:', err);
      }
    };
    fetchTemplates();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  const availableCards = useMemo(() => {
    const templateCards = [];
    templatesWithSections.forEach((template) => {
      const sections = template.sections || [];
      sections.forEach((section) => {
        const sectionKey = section.key;
        const icons = SECTION_KEY_ICONS[sectionKey] || DEFAULT_SECTION_ICON;
        const templateName = template.templateName || 'Template';
        const sectionTitle = section.title || sectionKey;
        templateCards.push({
          id: `${template._id}-${sectionKey}-map`,
          name: `${templateName} – ${sectionTitle} Map`,
          description: `Interactive map for ${sectionTitle}`,
          category: 'demand',
          icon: icons.map,
          preview: `Map showing ${sectionTitle} data`,
          sectionKey,
          templateId: template._id,
          templateName,
          type: 'map',
        });
        templateCards.push({
          id: `${template._id}-${sectionKey}-data`,
          name: `${templateName} – ${sectionTitle} Data`,
          description: `Data blocks for ${sectionTitle}`,
          category: 'demand',
          icon: icons.data,
          preview: `${sectionTitle} data blocks`,
          sectionKey,
          templateId: template._id,
          templateName,
          type: 'data',
        });
      });
    });
    return [...STATIC_CARDS, ...templateCards];
  }, [templatesWithSections]);

  const sectionTitleByKey = useMemo(() => {
    const map = {};
    templatesWithSections.forEach((template) => {
      (template.sections || []).forEach((section) => {
        map[`${template._id}-${section.key}`] = section.title || section.key;
      });
    });
    return map;
  }, [templatesWithSections]);

  useEffect(() => {
    if (persistedState?.searchResults && originalSearchResults === null) {
      setOriginalSearchResults(persistedState.searchResults);
      const radius = persistedState.searchRadius ?? persistedState.filters?.searchRadius;
      if (radius != null) {
        const num = typeof radius === 'string' ? parseFloat(radius) : radius;
        if (typeof num === 'number' && !isNaN(num)) setOriginalSearchRadius(num);
      }
    }
  }, [persistedState, originalSearchResults]);

  const hasRadiusGeometry = geometries.some(g => g.type === 'radius');
  const DEFAULT_RADIUS_MILES = 5;
  useEffect(() => {
    if (hasRadiusGeometry) return;
    if (searchResults == null && originalSearchResults == null) return;
    setSearchRadius(DEFAULT_RADIUS_MILES);
    setOriginalSearchRadius(DEFAULT_RADIUS_MILES);
    if (originalSearchResults?.buildings && Array.isArray(originalSearchResults.buildings)) {
      const filtered = originalSearchResults.buildings.filter(building => {
        const distance = building.distance ?? building.distanceInMiles;
        if (distance == null || distance === '') return false;
        const num = typeof distance === 'number' ? distance : parseFloat(distance);
        return !isNaN(num) && num <= DEFAULT_RADIUS_MILES;
      });
      setSearchResults({
        ...originalSearchResults,
        buildings: filtered,
        count: filtered.length,
        radius: DEFAULT_RADIUS_MILES,
      });
    }
  }, [hasRadiusGeometry]);

  const serializeFiltersAndLayout = () => {
    const filters = {
      searchRadius,
      unitType,
      bathroomType,
      rentPsfMin,
      rentPsfMax,
      rentBasis,
      timeWindow,
      appliedPropertyFilters,
      activePresets,
      geometries,
    };

    const layout = {
      activeCards,
    };

    return { filters, layout };
  };

  const getCurrentFilters = () => {
    return {
      searchRadius,
      unitType,
      bathroomType,
      rentPsfMin,
      rentPsfMax,
      rentBasis,
      timeWindow,
      appliedPropertyFilters,
      activePresets,
      geometries,
    };
  };

  const getCurrentLayout = () => {
    return {
      activeCards,
    };
  };

  const getCurrentDiscoverState = () => {
    const state = {
      searchQuery,
      searchCoordinates: searchResults?.searchCoordinates || null,
      searchRadius,
      searchResults: searchResults, // Include search results in share link
      filters: getCurrentFilters(),
      layout: getCurrentLayout(),
      sectionDataState,
      webmapIDs: {
        demographics: demographicsWebmapID,
        income: incomeWebmapID,
        expansion: expansionWebmapID,
        crime: crimeWebmapID,
      },
      addressInfo: {
        demographics: demographicsAddressInfo,
        income: incomeAddressInfo,
        expansion: expansionAddressInfo,
        crime: crimeAddressInfo,
      },
    };
    if (mapViewRef.current) {
      try {
        const v = mapViewRef.current;
        state.mapView = {
          center: [v.center.longitude, v.center.latitude],
          zoom: v.zoom,
        };
      } catch (_) {}
    }
    return state;
  };

  getDiscoverStateRef.current = getCurrentDiscoverState;

  useEffect(() => {
    if (sharedMode) return;
    return () => {
      try {
        const getState = getDiscoverStateRef.current;
        const state = getState?.();
        if (!state) return;
        savePersistedDiscoverState(state);
      } catch (e) {
        try {
          const getState = getDiscoverStateRef.current;
          const state = getState?.();
          if (state?.searchResults?.buildings) {
            const smaller = {
              ...state,
              searchResults: state.searchResults
                ? {
                    ...state.searchResults,
                    buildings: [],
                    count: state.searchResults.count ?? 0,
                  }
                : null,
            };
            savePersistedDiscoverState(smaller);
          }
        } catch (e2) {
          console.warn('[Discover] Could not persist state on unmount', e2);
        }
      }
    };
  }, [sharedMode]);

  const handleRadiusChange = (newRadius) => {
    let radiusValue = newRadius;
    if (typeof newRadius === 'string') {
      radiusValue = parseFloat(newRadius);
    }
    
    if (typeof radiusValue !== 'number' || isNaN(radiusValue) || radiusValue <= 0) {
      return;
    }
    
    newRadius = radiusValue;

    if (!searchQuery || typeof searchQuery !== 'string' || !searchQuery.trim()) {
      setSearchRadius(newRadius);
      return;
    }

    if (!originalSearchResults || originalSearchRadius === null || typeof originalSearchRadius !== 'number') {
      setSearchRadius(newRadius);
      return;
    }

    if (newRadius > originalSearchRadius) {
      setSearchRadius(newRadius);
      handleSearch(newRadius);
    } else if (newRadius < originalSearchRadius) {
      setSearchRadius(newRadius);
      if (!Array.isArray(originalSearchResults.buildings)) {
        return;
      }
      
      const filteredBuildings = originalSearchResults.buildings.filter(building => {
        if (!building || typeof building !== 'object') return false;
        
        const distance = building.distance !== null && building.distance !== undefined && building.distance !== ''
          ? building.distance
          : (building.distanceInMiles !== null && building.distanceInMiles !== undefined && building.distanceInMiles !== ''
              ? building.distanceInMiles
              : null);
        
        if (distance === null) return false;
        const numDistance = typeof distance === 'number' ? distance : parseFloat(distance);
        return !isNaN(numDistance) && numDistance <= newRadius;
      });

      setSearchResults({
        searchAddress: originalSearchResults.searchAddress,
        searchCoordinates: originalSearchResults.searchCoordinates,
        buildings: filteredBuildings,
        count: filteredBuildings.length,
        radius: newRadius,
      });
    } else {
      setSearchRadius(newRadius);
    }
  };

  const applyTemplateState = template => {
    if (!template || !template.filters || !template.layout) {
      return;
    }

    const nextFilters = template.filters;
    const nextLayout = template.layout;

    if (typeof nextFilters.searchRadius === 'number') {
      setSearchRadius(nextFilters.searchRadius);
    }
    if (typeof nextFilters.unitType === 'string') {
      setUnitType(nextFilters.unitType);
    }
    if (typeof nextFilters.bathroomType === 'string') {
      setBathroomType(nextFilters.bathroomType);
    }
    if (typeof nextFilters.rentPsfMin === 'string') {
      setRentPsfMin(nextFilters.rentPsfMin);
    }
    if (typeof nextFilters.rentPsfMax === 'string') {
      setRentPsfMax(nextFilters.rentPsfMax);
    }
    if (typeof nextFilters.rentBasis === 'string') {
      setRentBasis(nextFilters.rentBasis);
    }
    if (typeof nextFilters.timeWindow === 'string') {
      setTimeWindow(nextFilters.timeWindow);
    }
    if (nextFilters.appliedPropertyFilters) {
      setAppliedPropertyFilters(nextFilters.appliedPropertyFilters);
      setPropertyFiltersActive(true);
    } else {
      setAppliedPropertyFilters(null);
      setPropertyFiltersActive(false);
    }
    if (Array.isArray(nextFilters.activePresets)) {
      setActivePresets(nextFilters.activePresets);
    }
    if (Array.isArray(nextFilters.geometries)) {
      setGeometries(nextFilters.geometries);
    }

    if (
      Array.isArray(nextLayout.activeCards) &&
      nextLayout.activeCards.length > 0
    ) {
      setActiveCards(nextLayout.activeCards);
    }
  };

  useEffect(() => {
    let mapView;
    const savedMapView = persistedState?.mapView;

    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/Graphic',
      'esri/layers/GraphicsLayer',
    ]).then(([Map, MapView, Graphic, GraphicsLayer]) => {
      const graphicsLayer = new GraphicsLayer();
      const map = new Map({
        basemap: 'gray-vector',
        layers: [graphicsLayer],
      });

      const initialCenter = Array.isArray(savedMapView?.center) && savedMapView.center.length >= 2
        ? savedMapView.center
        : [-98.5795, 39.8283];
      const initialZoom = typeof savedMapView?.zoom === 'number' && !isNaN(savedMapView.zoom)
        ? savedMapView.zoom
        : 4;

      mapView = new MapView({
        container: mapRef.current,
        map: map,
        center: initialCenter,
        zoom: initialZoom,
      });

      mapViewRef.current = mapView;
      window.mapViewRef = mapView;
      window.graphicsLayerRef = graphicsLayer;
    });

    return () => {
      if (mapView) {
        mapView.destroy();
      }
    };
  }, []);

  useEffect(() => {
    const initializeAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('Google Maps API is not available, will retry...');
        initializationTimeoutRef.current = setTimeout(
          initializeAutocomplete,
          500,
        );
        return;
      }

      if (!addressInputRef.current) {
        console.log('Address input ref not available yet, will retry...');
        initializationTimeoutRef.current = setTimeout(
          initializeAutocomplete,
          100,
        );
        return;
      }

      if (autocompleteInitialized && autocompleteRef.current) {
        console.log('Autocomplete already initialized.');
        return;
      }

      if (autocompleteRef.current) {
        try {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current,
          );
          autocompleteRef.current = null;
        } catch (error) {
          console.error('Error cleaning up existing autocomplete:', error);
        }
      }

      console.log('Initializing Google Places Autocomplete for address field.');

      try {
        const options = {
          componentRestrictions: { country: 'us' },
          fields: [
            'address_components',
            'geometry',
            'icon',
            'name',
            'formatted_address',
            'place_id',
          ],
        };

        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          options,
        );

        const placeChangedHandler = () => {
          console.log('place_changed event triggered for address field');

          setTimeout(() => {
            try {
              const place = autocompleteRef.current?.getPlace();
              console.log('Place object received:', place);

              if (
                place &&
                place.formatted_address &&
                place.place_id &&
                place.formatted_address.trim() !== ''
              ) {
                const address = place.formatted_address;
                console.log(
                  '✓ Valid autocomplete suggestion selected:',
                  address,
                );
                setSearchQuery(address);
              } else {
                console.log(
                  '✗ Invalid place selection - missing required data:',
                  {
                    hasPlace: !!place,
                    hasFormattedAddress: !!place?.formatted_address,
                    hasPlaceId: !!place?.place_id,
                    formattedAddress: place?.formatted_address,
                  },
                );
              }
            } catch (error) {
              console.error('Error in place_changed handler:', error);
            }
          }, 10);
        };

        autocompleteRef.current.addListener(
          'place_changed',
          placeChangedHandler,
        );

        setAutocompleteInitialized(true);
        console.log(
          '✓ Autocomplete successfully initialized for address field.',
        );
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
        setAutocompleteInitialized(false);
        initializationTimeoutRef.current = setTimeout(
          initializeAutocomplete,
          1000,
        );
      }
    };

    initializeAutocomplete();

    return () => {
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }

      if (autocompleteRef.current) {
        console.log('Cleaning up autocomplete listeners.');
        try {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current,
          );
        } catch (error) {
          console.error('Error cleaning up autocomplete:', error);
        }
        autocompleteRef.current = null;
      }
      setAutocompleteInitialized(false);
    };
  }, []);

  const addCard = cardId => {
    if (!activeCards.find(c => c.id === cardId)) {
      setActiveCards([
        { id: cardId, visible: true, pinned: false },
        ...activeCards,
      ]);
    }
  };

  const removeCard = cardId => {
    setActiveCards(activeCards.filter(c => c.id !== cardId));
  };

  const toggleCardVisibility = cardId => {
    setActiveCards(
      activeCards.map(c =>
        c.id === cardId ? { ...c, visible: !c.visible } : c,
      ),
    );
  };

  const toggleCardPin = cardId => {
    setActiveCards(
      activeCards.map(c => (c.id === cardId ? { ...c, pinned: !c.pinned } : c)),
    );
  };

  const duplicateCard = cardId => {
    const card = activeCards.find(c => c.id === cardId);
    if (card) {
      const newCard = { ...card, id: `${cardId}-copy-${Date.now()}` };
      const index = activeCards.findIndex(c => c.id === cardId);
      setActiveCards([
        ...activeCards.slice(0, index + 1),
        newCard,
        ...activeCards.slice(index + 1),
      ]);
    }
  };

  const handleDragStart = cardId => {
    setDraggedCard(cardId);
  };

  const handleDragOver = (e, targetCardId) => {
    e.preventDefault();
    if (!draggedCard || draggedCard === targetCardId) return;

    const draggedIndex = activeCards.findIndex(c => c.id === draggedCard);
    const targetIndex = activeCards.findIndex(c => c.id === targetCardId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newCards = [...activeCards];
    const [removed] = newCards.splice(draggedIndex, 1);
    newCards.splice(targetIndex, 0, removed);
    setActiveCards(newCards);
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
  };

  const handleApplyPreset = preset => {
    if (activePresets.includes(preset.id)) {
      setActivePresets(activePresets.filter(id => id !== preset.id));
      if (
        appliedPropertyFilters &&
        appliedPropertyFilters.presetId === preset.id
      ) {
        setAppliedPropertyFilters(null);
        setPropertyFiltersActive(false);
      }
    } else {
      setActivePresets([...activePresets, preset.id]);
      if (preset.propertyTypes && preset.propertyTypes.length > 0) {
        const filters = {
          propertyTypes: preset.propertyTypes,
          presetId: preset.id,
        };
        setAppliedPropertyFilters(filters);
        setPropertyFiltersActive(true);
        handleApplyPropertyFilters(filters);
      }
    }
  };

  const handleApplyTemplate = template => {
    console.log('Applying template:', template);
    applyTemplateState(template);
    setShowTemplatesManager(false);
    if (searchQuery && searchQuery.trim()) {
      handleSearch();
    }
  };

  const handleOpenView = view => {
    console.log('Opening view:', view);
    if (!view) {
      setShowTemplatesManager(false);
      return;
    }

    applyTemplateState(view);

    if (view.searchAddress) {
      setSearchQuery(view.searchAddress);
    }

    if (
      view.snapshot &&
      Array.isArray(view.snapshot) &&
      view.snapshot.length > 0
    ) {
      console.log('[Discover] Applying saved view snapshot');
      const snapshotResults = {
        searchAddress: view.searchAddress,
        searchCoordinates: view.searchCoordinates,
        radius: view.radius,
        buildings: view.snapshot,
        count: view.snapshot.length,
      };
      setSearchResults(snapshotResults);
      setOriginalSearchResults(snapshotResults);
      setOriginalSearchRadius(view.radius || searchRadius);
      setUseExtentFiltering(false);
      setExtentFilteredBuildings(null);
      setSectionDataState({});
      zoomMapToLocation(view.searchCoordinates, view.snapshot);
    }

    setShowTemplatesManager(false);
  };

  const hasActivePropertyFilters = filters => {
    if (!filters) return false;
    const hasPropertyTypes =
      filters.propertyTypes && filters.propertyTypes.length > 0;
    const hasAmenities =
      filters.amenities && filters.amenities.length > 0;
    const unitCount = filters.unitCount || [5, 500];
    const stories = filters.stories || [1, 20];
    const yearBuilt = filters.yearBuilt || [1980, 2024];
    const isDefaultUnitCount =
      unitCount[0] === 5 && unitCount[1] === 500;
    const isDefaultStories =
      stories[0] === 1 && stories[1] === 20;
    const isDefaultYearBuilt =
      yearBuilt[0] === 1980 && yearBuilt[1] === 2024;
    return (
      hasPropertyTypes ||
      hasAmenities ||
      !isDefaultUnitCount ||
      !isDefaultStories ||
      !isDefaultYearBuilt
    );
  };

  const handleApplyPropertyFilters = filters => {
    console.log('Applying property filters:', filters);
    const active = hasActivePropertyFilters(filters);
    setAppliedPropertyFilters(active ? filters : null);
    setPropertyFiltersActive(active);
    setShowPropertyFilters(false);
  };

  const handleClearPropertyFilters = () => {
    setAppliedPropertyFilters(null);
    setPropertyFiltersActive(false);
  };

  const handleClearAll = () => {
    setSearchResults(null);
    setOriginalSearchResults(null);
    setOriginalSearchRadius(null);
    setUseExtentFiltering(false);
    setExtentFilteredBuildings(null);
    setRadiusFilterActive(false);
    setSearchQuery('');
    setDemographicsData(null);
    setDemographicsAddressInfo(null);
    setIncomeData(null);
    setIncomeAddressInfo(null);
    setExpansionData(null);
    setExpansionAddressInfo(null);
    setCrimeData(null);
    setCrimeAddressInfo(null);
    setSectionDataState({});
    setActiveCards(DEFAULT_CARDS.map(c => ({ ...c })));
    if (mapViewRef.current) {
      const graphicsLayer = mapViewRef.current.map.layers.items.find(
        layer => layer.type === 'graphics',
      );
      if (graphicsLayer) {
        graphicsLayer.removeAll();
      }
    }
    if (!sharedMode) {
      const defaultLayout = { activeCards: DEFAULT_CARDS.map(c => ({ ...c })) };
      const clearedState = {
        searchQuery: '',
        searchCoordinates: null,
        searchRadius: searchRadius,
        searchResults: null,
        filters: getCurrentFilters(),
        layout: defaultLayout,
        sectionDataState: {},
        webmapIDs: {
          demographics: '',
          income: '',
          expansion: '',
          crime: '',
        },
        addressInfo: {
          demographics: null,
          income: null,
          expansion: null,
          crime: null,
        },
      };
      if (mapViewRef.current) {
        try {
          const v = mapViewRef.current;
          clearedState.mapView = {
            center: [v.center.longitude, v.center.latitude],
            zoom: v.zoom,
          };
        } catch (_) {}
      }
      savePersistedDiscoverState(clearedState);
    }
  };

  const showClearAllButton =
    searchResults != null || activeCards.length > 0;

  const zoomMapToLocation = (searchCoordinates, buildings) => {
    if (!mapViewRef.current) return;

    const allCoordinates = [];

    if (
      searchCoordinates &&
      typeof searchCoordinates.longitude === 'number' &&
      typeof searchCoordinates.latitude === 'number'
    ) {
      allCoordinates.push([
        searchCoordinates.longitude,
        searchCoordinates.latitude,
      ]);
    }

    if (Array.isArray(buildings)) {
      buildings
        .filter(b => b.coordinates?.latitude && b.coordinates?.longitude)
        .forEach(b => {
          allCoordinates.push([
            b.coordinates.longitude,
            b.coordinates.latitude,
          ]);
        });
    }

    if (allCoordinates.length === 0) return;

    const lngs = allCoordinates.map(c => c[0]);
    const lats = allCoordinates.map(c => c[1]);
    const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;

    // Wait for the view to be ready before calling goTo
    const view = mapViewRef.current;
    if (view && typeof view.when === 'function') {
      view
        .when(() => {
          view.goTo({
            center: [centerLng, centerLat],
            zoom: 12,
          });
        })
        .catch(err => {
          console.error('[Discover] Error zooming map:', err);
        });
    } else {
      // Fallback: try to call goTo directly if when() is not available
      try {
        view.goTo({
          center: [centerLng, centerLat],
          zoom: 12,
        });
      } catch (err) {
        console.error('[Discover] Error zooming map:', err);
      }
    }
  };

  function parseCardId(cardId) {
    if (!cardId || typeof cardId !== 'string') return null;
    const legacyMatch = cardId.match(/^(demographics|income|expansion|crime)-(map|data)$/);
    if (legacyMatch) {
      return { templateId: DEFAULT_TEMPLATE_ID, sectionKey: legacyMatch[1] };
    }
    const match = cardId.match(/^([a-f0-9]{24})-(.+)-(map|data)$/);
    if (match) {
      return { templateId: match[1], sectionKey: match[2] };
    }
    return null;
  }

  const getRequiredSections = useCallback((activeCardsList) => {
    const seen = new Set();
    const list = [];
    activeCardsList.forEach((card) => {
      const parsed = parseCardId(card.id);
      if (parsed) {
        const key = `${parsed.templateId}-${parsed.sectionKey}`;
        if (!seen.has(key)) {
          seen.add(key);
          list.push({ templateId: parsed.templateId, sectionKey: parsed.sectionKey });
        }
      }
    });
    return list;
  }, []);

  const fetchSectionData = useCallback(
    async (templateId, sectionKey, address) => {
      if (!address || address.trim() === '') return;

      const stateKey = `${templateId}-${sectionKey}`;
      setSectionDataState((prev) => ({
        ...prev,
        [stateKey]: { ...prev[stateKey], loading: true },
      }));

      if (sectionKey === 'expansion') {
        setExpansionSectionLoading(true);
      }

      try {
        const token = await getAccessToken();
        const url = `${process.env.REACT_APP_NODE_URL}/discover/section`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            address: address.trim(),
            templateId,
            sectionKey,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[Discover] Section API error:`, response.status, errorText);
          setSectionDataState((prev) => ({
            ...prev,
            [stateKey]: { ...prev[stateKey], loading: false },
          }));
          return;
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const errorText = await response.text();
          console.error('[Discover] Section API returned non-JSON:', errorText?.substring(0, 200));
          setSectionDataState((prev) => ({
            ...prev,
            [stateKey]: { ...prev[stateKey], loading: false },
          }));
          return;
        }

        const result = await response.json();

        if (result.status && result.data) {
          const knownKeys = ['demographics', 'income', 'expansion', 'crime', 'schools'];
          const sectionData = sectionKey === 'schools'
            ? result.data
            : knownKeys.includes(sectionKey)
              ? result.data[sectionKey]
              : result.data.blocks != null
                ? { blocks: result.data.blocks }
                : result.data;
          if (result.data.arcgisToken) {
            setArcgisToken(result.data.arcgisToken);
          }
          setSectionDataState((prev) => ({
            ...prev,
            [stateKey]: {
              data: sectionData,
              webmapID: result.data.webmapID || '',
              addressInfo: result.data.addressInfo || null,
              loading: false,
            },
          }));
          if (sectionKey === 'demographics' && result.data.demographics != null) {
            setDemographicsData(result.data.demographics);
            setDemographicsWebmapID(result.data.webmapID || '');
            setDemographicsAddressInfo(result.data.addressInfo || null);
          } else if (sectionKey === 'income' && result.data.income != null) {
            setIncomeData(result.data.income);
            setIncomeWebmapID(result.data.webmapID || '');
            setIncomeAddressInfo(result.data.addressInfo || null);
          } else if (sectionKey === 'expansion' && result.data.expansion != null) {
            setExpansionData(result.data.expansion);
            setExpansionWebmapID(result.data.webmapID || '');
            setExpansionAddressInfo(result.data.addressInfo || null);
          } else if (sectionKey === 'crime' && result.data.crime != null) {
            setCrimeData(result.data.crime);
            setCrimeWebmapID(result.data.webmapID || '');
            setCrimeAddressInfo(result.data.addressInfo || null);
          }
        } else {
          console.warn('[Discover] Section API returned status: false', result.message);
          setSectionDataState((prev) => ({
            ...prev,
            [stateKey]: { ...prev[stateKey], loading: false },
          }));
        }
      } catch (error) {
        console.error('[Discover] Error fetching section data:', error);
        setSectionDataState((prev) => ({
          ...prev,
          [stateKey]: { ...prev[stateKey], loading: false },
        }));
      } finally {
        if (sectionKey === 'expansion') {
          setExpansionSectionLoading(false);
        }
      }
    },
    [getAccessToken],
  );

  const handleSearch = async (radiusOverride = null) => {
    if (!searchQuery || typeof searchQuery !== 'string' || searchQuery.trim() === '') {
      setSearchError('Please enter an address to search');
      return;
    }

    let radiusToUse = radiusOverride !== null ? radiusOverride : searchRadius;
    
    if (typeof radiusToUse === 'string') {
      radiusToUse = parseFloat(radiusToUse);
    }
    
    if (typeof radiusToUse !== 'number' || isNaN(radiusToUse) || radiusToUse <= 0) {
      console.warn('[Discover] Invalid radius value:', radiusToUse, 'type:', typeof radiusToUse, 'using default 5');
      radiusToUse = 5;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchResults(null);
    setOriginalSearchResults(null);
    setOriginalSearchRadius(null);
    setDemographicsData(null);
    setDemographicsAddressInfo(null);
    setIncomeData(null);
    setIncomeAddressInfo(null);
    setExpansionData(null);
    setExpansionAddressInfo(null);
    setCrimeData(null);
    setCrimeAddressInfo(null);
    setSectionDataState({});

    try {
      const accessToken = getAccessToken();
      const url = `${process.env.REACT_APP_NODE_URL}/discover/search`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          address: String(searchQuery).trim(),
          radius: Number(radiusToUse),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Failed to search buildings');
      }

      setSearchResults(data.data);
      setOriginalSearchResults(data.data);
      setOriginalSearchRadius(radiusToUse);
      setUseExtentFiltering(false);
      setExtentFilteredBuildings(null);
      setRadiusFilterActive(true);

      if (data.data.buildings.length > 0) {
        zoomMapToLocation(data.data.searchCoordinates, data.data.buildings);
      }

      const requiredSectionsList = getRequiredSections(activeCards);
      await Promise.all(
        requiredSectionsList.map(({ templateId, sectionKey }) =>
          fetchSectionData(templateId, sectionKey, searchQuery.trim()),
        ),
      );
    } catch (error) {
      const errorMessage = error?.message || error?.toString() || 'Failed to search buildings. Please try again.';
      console.error('Error searching buildings:', errorMessage);
      setSearchError(errorMessage);
    } finally {
      setSearchLoading(false);
    }
  };

  const buildingsForFiltering = useMemo(() => {
    if (!originalSearchResults || !originalSearchResults.buildings) {
      return searchResults?.buildings || [];
    }

    if (radiusFilterActive) {
      const radius =
        typeof searchRadius === 'number' && !isNaN(searchRadius)
          ? searchRadius
          : originalSearchRadius || 5;
      return originalSearchResults.buildings.filter(building => {
        const distance =
          building.distance ?? building.distanceInMiles ?? null;
        if (distance == null || distance === '') return false;
        const numDist =
          typeof distance === 'number' ? distance : parseFloat(distance);
        return !isNaN(numDist) && numDist <= radius;
      });
    }

    if (useExtentFiltering && extentFilteredBuildings !== null) {
      return extentFilteredBuildings;
    }

    return searchResults?.buildings || [];
  }, [
    originalSearchResults,
    radiusFilterActive,
    searchRadius,
    originalSearchRadius,
    useExtentFiltering,
    extentFilteredBuildings,
    searchResults,
  ]);

  const filteredBuildings = useMemo(() => {
    if (!buildingsForFiltering || buildingsForFiltering.length === 0) return [];

    const minPsf = rentPsfMin !== '' ? parseFloat(rentPsfMin) : null;
    const maxPsf = rentPsfMax !== '' ? parseFloat(rentPsfMax) : null;

    return buildingsForFiltering
      .map(building => {
        let filteredUnits = building.units || [];

        if (unitType !== 'all-beds') {
          const bedFilter = {
            studio: 0,
            '1br': 1,
            '2br': 2,
            '3br': 3,
            '4br': 4,
            '5br': 5,
          }[unitType];

          if (bedFilter !== undefined) {
            filteredUnits = filteredUnits.filter(
              unit => (unit.bedrooms || 0) === bedFilter,
            );
          }
        }

        if (bathroomType !== 'all-baths') {
          const bathFilter = {
            '1ba': 1,
            '1.5ba': 1.5,
            '2ba': 2,
            '2.5ba': 2.5,
            '3ba': 3,
            '3.5ba': 3.5,
            '4ba': 4,
          }[bathroomType];

          if (bathFilter !== undefined) {
            if (bathroomType === '4ba') {
              filteredUnits = filteredUnits.filter(
                unit => (unit.bathrooms || 0) >= 4,
              );
            } else {
              filteredUnits = filteredUnits.filter(
                unit => (unit.bathrooms || 0) === bathFilter,
              );
            }
          }
        }

        if (minPsf !== null || maxPsf !== null) {
          filteredUnits = filteredUnits.filter(unit => {
            const sqft = unit.squareFeet || unit.sqft;
            const rent = unit.rent;
            if (!sqft || sqft <= 0 || !rent) return false;
            const rentPsf = rent / sqft;
            if (Number.isNaN(rentPsf)) return false;
            if (minPsf !== null && rentPsf < minPsf) return false;
            if (maxPsf !== null && rentPsf > maxPsf) return false;
            return true;
          });
        }

        if (filteredUnits.length === 0) return null;

        const occupied = filteredUnits.filter(
          u => u.status === 'occupied' || u.status === 'leased',
        ).length;
        const vacant = filteredUnits.filter(
          u => u.status === 'vacant' || u.status === 'available',
        ).length;

        return {
          ...building,
          units: filteredUnits,
          occupied,
          vacant,
          totalUnits: building.totalUnits ?? filteredUnits.length,
        };
      })
      .filter(Boolean);
  }, [buildingsForFiltering, unitType, bathroomType, rentPsfMin, rentPsfMax]);

  const createCirclePolygon = (centerLng, centerLat, radiusMeters) => {
    const points = 64;
    const ring = [];
    const earthRadius = 6378137;

    for (let i = 0; i <= points; i++) {
      const angle = (i * 360) / points;
      const dx = radiusMeters * Math.cos((angle * Math.PI) / 180);
      const dy = radiusMeters * Math.sin((angle * Math.PI) / 180);

      const latOffset = dy / earthRadius;
      const lngOffset = dx / (earthRadius * Math.cos((centerLat * Math.PI) / 180));

      const lat = centerLat + (latOffset * 180) / Math.PI;
      const lng = centerLng + (lngOffset * 180) / Math.PI;

      ring.push([lng, lat]);
    }

    return [ring];
  };

  useEffect(() => {
    console.log('[Discover] Map markers useEffect triggered', {
      hasMapView: !!mapViewRef.current,
      hasSearchResults: !!searchResults,
      searchResultsType: searchResults ? typeof searchResults : 'null',
      filteredBuildingsCount: filteredBuildings?.length || 0,
    });

    if (!searchResults) {
      console.log(
        '[Discover] Map markers useEffect: no searchResults, returning early',
      );
      return;
    }

    // Define updateMapMarkers function first
    const updateMapMarkers = async () => {
      const view = mapViewRef.current;
      if (!view) {
        console.log('[Discover] Map view not available yet');
        return;
      }

      // Wait for view to be ready, then add markers
      const addMarkersToMap = async () => {
        try {
          const [Graphic] = await loadModules(['esri/Graphic']);

          // Try to get graphics layer from map layers
          let graphicsLayer = null;
          if (view.map && view.map.layers && view.map.layers.items) {
            graphicsLayer = view.map.layers.items.find(
              layer => layer.type === 'graphics',
            );
          }

          // Fallback: use window.graphicsLayerRef if available
          if (!graphicsLayer && window.graphicsLayerRef) {
            graphicsLayer = window.graphicsLayerRef;
          }

          if (!graphicsLayer) {
            console.warn(
              '[Discover] Graphics layer not found. Map layers:',
              view.map?.layers?.items?.length || 0,
            );
            // Retry after a delay
            setTimeout(() => {
              if (mapViewRef.current && searchResults) {
                updateMapMarkers();
              }
            }, 200);
            return;
          }

          console.log('[Discover] Adding markers to map', {
            searchCoordinates: searchResults.searchCoordinates,
            buildingsCount: filteredBuildings.length,
            radius: searchRadius,
          });

          graphicsLayer.removeAll();

          if (searchResults.searchCoordinates) {
            const centerLongitude = searchResults.searchCoordinates.longitude;
            const centerLatitude = searchResults.searchCoordinates.latitude;
            const radiusInMeters = searchRadius * 1609.34;

            const searchLocationGraphic = new Graphic({
              geometry: {
                type: 'point',
                longitude: centerLongitude,
                latitude: centerLatitude,
              },
              symbol: {
                type: 'simple-marker',
                style: 'circle',
                color: '#EF4444',
                size: '16px',
                outline: {
                  color: '#FFFFFF',
                  width: 3,
                },
              },
            });

            graphicsLayer.add(searchLocationGraphic);

            const radiusCircleGraphic = new Graphic({
              geometry: {
                type: 'polyline',
                paths: [createCirclePolygon(
                  centerLongitude,
                  centerLatitude,
                  radiusInMeters,
                )[0]],
                spatialReference: { wkid: 4326 },
              },
              symbol: {
                type: 'simple-line',
                color: [239, 68, 68, 255],
                width: 2,
                style: 'solid',
              },
            });

            graphicsLayer.add(radiusCircleGraphic);
            console.log('[Discover] Added search location marker and radius circle');
          }

          let addedCount = 0;
          filteredBuildings.forEach(building => {
            if (
              building.coordinates?.latitude &&
              building.coordinates?.longitude
            ) {
              const buildingGraphic = new Graphic({
                geometry: {
                  type: 'point',
                  longitude: building.coordinates.longitude,
                  latitude: building.coordinates.latitude,
                },
                symbol: {
                  type: 'simple-marker',
                  style: 'circle',
                  color: '#39585C',
                  size: '12px',
                  outline: {
                    color: '#FFFFFF',
                    width: 2,
                  },
                },
                attributes: {
                  buildingId: building.id,
                  name: building.name,
                  address: building.address,
                },
              });

              graphicsLayer.add(buildingGraphic);
              addedCount++;
            }
          });

          console.log(`[Discover] Added ${addedCount} building markers`);
        } catch (error) {
          console.error('[Discover] Error updating map markers:', error);
        }
      };

      // Check if view is ready, if not wait for it
      if (typeof view.when === 'function') {
        // Check if view is already ready
        if (view.ready) {
          addMarkersToMap();
        } else {
          view
            .when(() => {
              addMarkersToMap();
            })
            .catch(err => {
              console.error('[Discover] Error waiting for map view:', err);
              // Fallback: try adding markers anyway after a delay
              setTimeout(addMarkersToMap, 500);
            });
        }
      } else {
        // Fallback: try adding markers after a delay
        setTimeout(addMarkersToMap, 500);
      }
    };

    // If map isn't ready yet, set up a retry mechanism
    if (!mapViewRef.current) {
      console.log(
        '[Discover] Map view not ready yet, will retry when map is available',
      );

      // Retry when map becomes available
      let retryCount = 0;
      const maxRetries = 50; // 50 * 200ms = 10 seconds max wait

      const checkMapAndRetry = () => {
        retryCount++;
        if (mapViewRef.current && searchResults) {
          console.log(
            '[Discover] Map is now available, updating markers (attempt',
            retryCount,
            ')',
          );
          // Map is ready, proceed with marker update
          updateMapMarkers();
        } else if (retryCount < maxRetries && searchResults) {
          // Keep retrying until map is ready
          setTimeout(checkMapAndRetry, 200);
        } else if (retryCount >= maxRetries) {
          console.error(
            '[Discover] Max retries reached, map view still not available',
          );
        }
      };

      // Start checking after a short delay
      const timeoutId = setTimeout(checkMapAndRetry, 200);

      // Cleanup timeout if component unmounts or dependencies change
      return () => clearTimeout(timeoutId);
    }

    // Map is ready, update markers immediately
    updateMapMarkers();
  }, [filteredBuildings, searchResults, searchRadius]);

  useEffect(() => {
    if (!mapViewRef.current || !originalSearchResults || !originalSearchResults.buildings || !searchResults) {
      setUseExtentFiltering(false);
      setExtentFilteredBuildings(null);
      return;
    }

    if (radiusFilterActive) {
      return;
    }

    const view = mapViewRef.current;
    let watchHandle = null;
    let isInitialZoom = true;
    let initialZoomTimeout = null;
    let debounceTimeout = null;

    const filterBuildingsByExtent = () => {
      if (isInitialZoom || radiusFilterActive) {
        return;
      }

      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      debounceTimeout = setTimeout(async () => {
        try {
          const extent = view.extent;
          if (!extent) {
            return;
          }

          // Convert extent to WGS84 if needed
          let extentWGS84 = extent;
          const extentSR = extent.spatialReference?.wkid;
          
          if (extentSR === 102100 || extentSR === 3857) {
            // Web Mercator to WGS84 conversion
            // Manual conversion: Web Mercator (102100/3857) to WGS84 (4326)
            const toLongitude = (mercatorX) => {
              return mercatorX / 20037508.34 * 180;
            };
            const toLatitude = (mercatorY) => {
              return Math.atan(Math.sinh(mercatorY / 20037508.34 * Math.PI)) * 180 / Math.PI;
            };
            
            extentWGS84 = {
              xmin: toLongitude(extent.xmin),
              xmax: toLongitude(extent.xmax),
              ymin: toLatitude(extent.ymin),
              ymax: toLatitude(extent.ymax),
            };
          } else if (extentSR === 4326) {
            // Already in WGS84, use directly
            extentWGS84 = {
              xmin: extent.xmin,
              xmax: extent.xmax,
              ymin: extent.ymin,
              ymax: extent.ymax,
            };
          } else {
            console.warn('[Discover] Unknown spatial reference:', extentSR, 'assuming WGS84');
            extentWGS84 = {
              xmin: extent.xmin,
              xmax: extent.xmax,
              ymin: extent.ymin,
              ymax: extent.ymax,
            };
          }

          console.log('[Discover] Filtering buildings by extent', {
            originalExtent: {
              xmin: extent.xmin,
              xmax: extent.xmax,
              ymin: extent.ymin,
              ymax: extent.ymax,
            },
            wgs84Extent: {
              xmin: extentWGS84.xmin,
              xmax: extentWGS84.xmax,
              ymin: extentWGS84.ymin,
              ymax: extentWGS84.ymax,
            },
            spatialReference: extent.spatialReference?.wkid,
            totalBuildings: originalSearchResults.buildings.length,
          });

          const filtered = originalSearchResults.buildings.filter(building => {
            if (!building.coordinates?.latitude || !building.coordinates?.longitude) {
              return false;
            }

            const lng = building.coordinates.longitude;
            const lat = building.coordinates.latitude;

            const isInExtent = (
              lng >= extentWGS84.xmin &&
              lng <= extentWGS84.xmax &&
              lat >= extentWGS84.ymin &&
              lat <= extentWGS84.ymax
            );

            return isInExtent;
          });

          console.log('[Discover] Extent filtered buildings', {
            filteredCount: filtered.length,
            totalCount: originalSearchResults.buildings.length,
          });

          if (filtered.length === 0 && originalSearchResults.buildings.length > 0) {
            console.log('[Discover] No buildings in extent, disabling extent filtering');
            setUseExtentFiltering(false);
            setExtentFilteredBuildings(null);
            return;
          }

          console.log('[Discover] Setting extent filtered buildings', {
            filteredCount: filtered.length,
            totalCount: originalSearchResults.buildings.length,
            useExtentFiltering: true,
          });
          setExtentFilteredBuildings(filtered);
          setUseExtentFiltering(true);
        } catch (error) {
          console.error('[Discover] Error filtering buildings by extent:', error);
          setUseExtentFiltering(false);
          setExtentFilteredBuildings(null);
        }
      }, 300);
    };

    if (typeof view.when === 'function') {
      view.when(() => {
        initialZoomTimeout = setTimeout(() => {
          isInitialZoom = false;
          console.log('[Discover] Initial zoom period ended, extent filtering enabled');
        }, 2000);

        watchHandle = view.watch('extent', () => {
          filterBuildingsByExtent();
        });

        console.log('[Discover] Map extent watching set up');
      }).catch(error => {
        console.error('[Discover] Error setting up map view:', error);
      });
    } else {
      // Fallback if view.when is not available
      const checkView = setInterval(() => {
        if (view && view.extent) {
          clearInterval(checkView);
          initialZoomTimeout = setTimeout(() => {
            isInitialZoom = false;
            console.log('[Discover] Initial zoom period ended, extent filtering enabled');
          }, 2000);

          watchHandle = view.watch('extent', () => {
            filterBuildingsByExtent();
          });

          console.log('[Discover] Map extent watching set up (fallback)');
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkView);
      }, 5000);
    }

    return () => {
      if (watchHandle) {
        watchHandle.remove();
      }
      if (initialZoomTimeout) {
        clearTimeout(initialZoomTimeout);
      }
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [originalSearchResults, searchResults, radiusFilterActive]);

  useEffect(() => {
    // Skip fetching webmap IDs in shared mode (not authenticated)
    if (sharedMode) {
      return;
    }

    const fetchWebmapIDs = async () => {
      try {
        const accessToken = getAccessToken();
        if (!accessToken) {
          return;
        }
        const url = `${process.env.REACT_APP_NODE_URL}/agent/webmapIDs`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await response.json();

        if (response.ok && Array.isArray(data)) {
          const demographicsSection = data.find(
            item => item.section === 'demographics',
          );
          if (demographicsSection) {
            setDemographicsWebmapID(demographicsSection.webmapID);
          }

          const incomeSection = data.find(item => item.section === 'income');
          if (incomeSection) {
            setIncomeWebmapID(incomeSection.webmapID);
          }

          const expansionSection = data.find(
            item => item.section === 'expansion',
          );
          if (expansionSection) {
            setExpansionWebmapID(expansionSection.webmapID);
          }

          const crimeSection = data.find(item => item.section === 'crime');
          if (crimeSection) {
            setCrimeWebmapID(crimeSection.webmapID);
          }
        }
      } catch (error) {
        console.error('Error fetching webmap IDs:', error);
      }
    };

    fetchWebmapIDs();
  }, [getAccessToken, sharedMode]);

  // In shared mode, fetch a public ArcGIS map token for embedded maps
  useEffect(() => {
    if (!sharedMode) return;
    if (arcgisToken) return;

    const fetchMapToken = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODE_URL}/share-link/map-token`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          console.error(
            '[Discover] Failed to fetch public map token for shared mode',
            response.status,
          );
          return;
        }

        const data = await response.json();
        if (data.status && data.token) {
          setArcgisToken(data.token);
        }
      } catch (err) {
        console.error('[Discover] Error fetching public map token:', err);
      }
    };

    fetchMapToken();
  }, [sharedMode, arcgisToken]);

  const previousCardIdsRef = useRef([]);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') return;
    if (!searchResults) return;

    const currentCardIds = activeCards.map(card => card.id);
    const requiredList = getRequiredSections(activeCards);
    const toFetch = requiredList.filter(({ templateId, sectionKey }) => {
      const stateKey = `${templateId}-${sectionKey}`;
      const existing = sectionDataState[stateKey];
      return !existing?.data && !existing?.loading;
    });

    if (toFetch.length > 0) {
      setSectionDataState((prev) => {
        const next = { ...prev };
        toFetch.forEach(({ templateId, sectionKey }) => {
          const stateKey = `${templateId}-${sectionKey}`;
          next[stateKey] = { ...prev[stateKey], loading: true };
        });
        return next;
      });
      toFetch.forEach(({ templateId, sectionKey }) =>
        fetchSectionData(templateId, sectionKey, searchQuery),
      );
    }

    previousCardIdsRef.current = currentCardIds;
  }, [
    activeCards,
    searchQuery,
    searchResults,
    sectionDataState,
    getRequiredSections,
    fetchSectionData,
  ]);

  // In shared mode, load search results from shared state if available
  useEffect(() => {
    console.log('[Discover] Shared mode searchResults loading', {
      sharedMode,
      hasSharedState: !!sharedState,
      hasSearchResults: !!searchResults,
      sharedStateSearchResults: !!sharedState?.searchResults,
    });

    if (sharedMode && sharedState && !searchResults) {
      // If search results are stored in the share link, use them directly
      if (sharedState.searchResults) {
        console.log('[Discover] Setting searchResults from sharedState', {
          hasBuildings: !!sharedState.searchResults.buildings,
          buildingsCount: sharedState.searchResults.buildings?.length || 0,
          hasCoordinates: !!sharedState.searchResults.searchCoordinates,
        });
        setSearchResults(sharedState.searchResults);
        setOriginalSearchResults(sharedState.searchResults);
        const sharedRadius = sharedState.searchRadius || sharedState.searchResults.radius || 5;
        setOriginalSearchRadius(sharedRadius);
        setUseExtentFiltering(false);
        setExtentFilteredBuildings(null);
      } else {
        console.warn(
          '[Discover] Shared state exists but no searchResults found',
        );
      }
      // Otherwise, we can't perform a search without authentication
      // The share link should ideally include search results
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedMode, sharedState]);

  // Zoom map to location when searchResults are loaded in shared mode
  useEffect(() => {
    if (
      sharedMode &&
      searchResults &&
      searchResults.searchCoordinates &&
      searchResults.buildings
    ) {
      // Wait for map to be initialized and ready, then zoom
      const checkAndZoom = () => {
        const view = mapViewRef.current;
        if (view) {
          // Use view.when() to ensure the view is fully ready
          if (typeof view.when === 'function') {
            view
              .when(() => {
                zoomMapToLocation(
                  searchResults.searchCoordinates,
                  searchResults.buildings,
                );
              })
              .catch(err => {
                console.error('[Discover] Error waiting for map view:', err);
              });
          } else {
            // Fallback: try zooming directly after a delay
            setTimeout(() => {
              zoomMapToLocation(
                searchResults.searchCoordinates,
                searchResults.buildings,
              );
            }, 500);
          }
        } else {
          // Retry after a short delay if map isn't initialized yet
          setTimeout(checkAndZoom, 100);
        }
      };

      const timeoutId = setTimeout(checkAndZoom, 100);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedMode, searchResults]);

  const visibleCards = activeCards.filter(c => c.visible);
  const sortedCards = [...visibleCards].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return 0;
  });

  const handleSave = async () => {
    if (!saveName.trim()) {
      setSaveError('Name is required');
      return;
    }

    const { filters, layout } = serializeFiltersAndLayout();

    try {
      setSaveLoading(true);
      setSaveError(null);
      const accessToken = getAccessToken();

      if (saveDialogType === 'template') {
        const response = await fetch(
          `${process.env.REACT_APP_NODE_URL}/discover/templates`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              name: saveName.trim(),
              description: saveDescription.trim() || null,
              filters,
              layout,
            }),
          },
        );

        const data = await response.json();
        if (!response.ok || !data.status) {
          throw new Error(data.message || 'Failed to save template');
        }
      } else {
        if (!searchResults) {
          setSaveError('You must run a search before saving a view');
          setSaveLoading(false);
          return;
        }

        const snapshot = (searchResults.buildings || []).map(building => ({
          id: building.id,
          propertyId: building.propertyId,
          name: building.name,
          address: building.address,
          street: building.street,
          city: building.city,
          state: building.state,
          zipCode: building.zipCode,
          coordinates: building.coordinates,
          totalUnits: building.totalUnits,
          occupied: building.occupied,
          vacant: building.vacant,
          propertyType: building.propertyType,
          buildingAmenities: building.buildingAmenities,
          units: building.units,
        }));

        const response = await fetch(
          `${process.env.REACT_APP_NODE_URL}/discover/views`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              name: saveName.trim(),
              description: saveDescription.trim() || null,
              filters,
              layout,
              searchAddress:
                searchResults.searchAddress || searchQuery.trim() || '',
              searchCoordinates: searchResults.searchCoordinates,
              radius: searchResults.radius || searchRadius,
              snapshot,
            }),
          },
        );

        const data = await response.json();
        if (!response.ok || !data.status) {
          throw new Error(data.message || 'Failed to save view');
        }
      }

      setShowSaveDialog(false);
      setSaveName('');
      setSaveDescription('');
    } catch (error) {
      console.error('Error saving discover configuration:', error);
      setSaveError(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleAddToPortfolio = () => {
    const address =
      (searchResults && searchResults.searchAddress) || searchQuery.trim();

    if (!address) {
      console.log('[Discover] Add to Portfolio clicked without an address');
      return;
    }

    console.log('[Discover] Navigating to Rent Advisor with address', address);
    navigate('/rent-advisor', {
      state: {
        fromDiscover: true,
        address,
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#F9F9F9',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ paddingX: { md: '1.25rem', xs: '0' } }}>
        <Header sideBarState={setSideBarState} pageTitle='Discover' />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #E5E7EB',
            paddingX: { xs: '1rem', md: '1.5rem' },
            paddingY: '1rem',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  maxWidth: { xs: '100%', sm: '448px' },
                  display: 'flex',
                  gap: '0.5rem',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <TextField
                  inputRef={addressInputRef}
                  placeholder='Search an address, city, ZIP code or place'
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                  }}
                  onKeyDown={e => {
                    if (
                      e.key === 'Enter' &&
                      !searchLoading &&
                      searchQuery.trim()
                    ) {
                      handleSearch();
                    }
                  }}
                  onFocus={() => {
                    console.log('Address input field focused');
                    if (
                      !autocompleteInitialized &&
                      window.google &&
                      window.google.maps &&
                      window.google.maps.places
                    ) {
                      console.log(
                        'Retrying autocomplete initialization on focus...',
                      );
                      setAutocompleteInitialized(false);
                    }
                  }}
                  fullWidth
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#FFFFFF',
                      borderRadius: '0.5rem',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <Search
                        style={{
                          marginRight: '0.5rem',
                          color: '#6B7280',
                          width: '16px',
                          height: '16px',
                        }}
                      />
                    ),
                  }}
                />
                <Button
                  variant='contained'
                  startIcon={<Search size={16} />}
                  onClick={handleSearch}
                  disabled={searchLoading || !searchQuery.trim()}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '0.5rem',
                    paddingX: { xs: '1rem', sm: '1.5rem' },
                    backgroundColor: '#39585C',
                    '&:hover': {
                      backgroundColor: '#2d4649',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#E5E7EB',
                      color: '#9CA3AF',
                      boxShadow: 'none',
                    },
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Box
                    component='span'
                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                  >
                    {searchLoading ? 'Searching...' : 'Search'}
                  </Box>
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginLeft: { xs: 0, sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  flexWrap: 'wrap',
                }}
              >
                {!sharedMode && (
                  <>
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<Share2 size={16} />}
                      onClick={() => setShowExportShare(true)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '0.5rem',
                        borderColor: '#39585C',
                        color: '#39585C',
                        '&:hover': {
                          borderColor: '#2d4649',
                          backgroundColor: '#F0F4F5',
                        },
                      }}
                    >
                      Export
                    </Button>
                    <Button
                      variant='outlined'
                      size='small'
                      startIcon={<FolderOpen size={16} />}
                      onClick={() => setShowTemplatesManager(true)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '0.5rem',
                        borderColor: '#39585C',
                        color: '#39585C',
                        '&:hover': {
                          borderColor: '#2d4649',
                          backgroundColor: '#F0F4F5',
                        },
                      }}
                    >
                      Load
                    </Button>
                    <Button
                      variant='contained'
                      size='small'
                      startIcon={<Save size={16} />}
                      endIcon={<ChevronDown size={16} />}
                      onClick={e => setSaveMenuAnchor(e.currentTarget)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '0.5rem',
                        backgroundColor: '#39585C',
                        '&:hover': {
                          backgroundColor: '#2d4649',
                        },
                      }}
                    >
                      Save
                    </Button>
                  </>
                )}
                {sharedMode && allowDownload && onExportPdf && (
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={
                      pdfGenerating ? (
                        <CircularProgress size={16} color='inherit' />
                      ) : (
                        <Download size={16} />
                      )
                    }
                    onClick={onExportPdf}
                    disabled={pdfGenerating}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '0.5rem',
                      borderColor: '#39585C',
                      color: '#39585C',
                      '&:hover': {
                        borderColor: '#2d4649',
                        backgroundColor: '#F0F4F5',
                      },
                    }}
                  >
                    {pdfGenerating ? 'Generating PDF...' : 'Export PDF'}
                  </Button>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant='outlined'
                size='small'
                startIcon={
                  showMapOnMobile ? <EyeOff size={16} /> : <Eye size={16} />
                }
                onClick={() => setShowMapOnMobile(!showMapOnMobile)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '0.5rem',
                  borderColor: showMapOnMobile ? '#39585C' : '#D1D5DB',
                  color: showMapOnMobile ? '#39585C' : '#111827',
                  backgroundColor: showMapOnMobile ? '#F0F4F5' : 'transparent',
                  '&:hover': {
                    borderColor: '#39585C',
                    backgroundColor: '#F0F4F5',
                  },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  display: { xs: 'flex', lg: 'none' },
                }}
              >
                {showMapOnMobile ? 'Hide Map' : 'Show Map'}
              </Button>

              <Button
                variant='outlined'
                size='small'
                startIcon={<Building2 size={16} />}
                onClick={() => setShowPropertyFilters(true)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '0.5rem',
                  borderColor: propertyFiltersActive ? '#39585C' : '#D1D5DB',
                  color: propertyFiltersActive ? '#39585C' : '#111827',
                  backgroundColor: propertyFiltersActive
                    ? '#F0F4F5'
                    : 'transparent',
                  '&:hover': {
                    borderColor: '#39585C',
                    backgroundColor: '#F0F4F5',
                  },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                <Box
                  component='span'
                  sx={{ display: { xs: 'none', sm: 'inline' } }}
                >
                  Property Filters
                </Box>
                <Box
                  component='span'
                  sx={{ display: { xs: 'inline', sm: 'none' } }}
                >
                  Filters
                </Box>
                {propertyFiltersActive && (
                  <Chip
                    label='Active'
                    size='small'
                    sx={{
                      marginLeft: '0.5rem',
                      height: '20px',
                      fontSize: '0.625rem',
                      backgroundColor: '#39585C',
                      color: '#FFFFFF',
                    }}
                  />
                )}
              </Button>
              {propertyFiltersActive && (
                <Button
                  variant='text'
                  size='small'
                  onClick={handleClearPropertyFilters}
                  sx={{
                    textTransform: 'none',
                    color: '#6B7280',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': {
                      color: '#39585C',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}

              <FormControl
                size='small'
                sx={{
                  minWidth: { xs: '100%', sm: '160px' },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Select
                  value={unitType}
                  onChange={e => setUnitType(e.target.value)}
                  sx={{
                    borderRadius: '0.5rem',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D1D5DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#39585C',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#39585C',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#39585C',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          '&:hover': {
                            backgroundColor: '#F0F4F5',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#F0F4F5',
                            color: '#39585C',
                            '&:hover': {
                              backgroundColor: '#F0F4F5',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value='all-beds'>All Beds</MenuItem>
                  <MenuItem value='studio'>Studio</MenuItem>
                  <MenuItem value='1br'>1 BR</MenuItem>
                  <MenuItem value='2br'>2 BR</MenuItem>
                  <MenuItem value='3br'>3 BR</MenuItem>
                  <MenuItem value='4br'>4 BR</MenuItem>
                  <MenuItem value='5br'>5 BR</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size='small'
                sx={{
                  minWidth: { xs: '100%', sm: '160px' },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Select
                  value={bathroomType}
                  onChange={e => setBathroomType(e.target.value)}
                  sx={{
                    borderRadius: '0.5rem',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D1D5DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#39585C',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#39585C',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#39585C',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          '&:hover': {
                            backgroundColor: '#F0F4F5',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#F0F4F5',
                            color: '#39585C',
                            '&:hover': {
                              backgroundColor: '#F0F4F5',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value='all-baths'>All Baths</MenuItem>
                  <MenuItem value='1ba'>1 BA</MenuItem>
                  <MenuItem value='1.5ba'>1.5 BA</MenuItem>
                  <MenuItem value='2ba'>2 BA</MenuItem>
                  <MenuItem value='2.5ba'>2.5 BA</MenuItem>
                  <MenuItem value='3ba'>3 BA</MenuItem>
                  <MenuItem value='3.5ba'>3.5 BA</MenuItem>
                  <MenuItem value='4ba'>4+ BA</MenuItem>
                </Select>
              </FormControl>

              <Box
                sx={{
                  display: 'flex',
                  gap: '0.5rem',
                  width: { xs: '100%', sm: 'auto' },
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  label='Min $/SF'
                  value={rentPsfMin}
                  onChange={e => setRentPsfMin(e.target.value)}
                  size='small'
                  type='number'
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    minWidth: { xs: '100%', sm: '140px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.5rem',
                      backgroundColor: '#FFFFFF',
                    },
                  }}
                />
                <TextField
                  label='Max $/SF'
                  value={rentPsfMax}
                  onChange={e => setRentPsfMax(e.target.value)}
                  size='small'
                  type='number'
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    minWidth: { xs: '100%', sm: '140px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.5rem',
                      backgroundColor: '#FFFFFF',
                    },
                  }}
                />
              </Box>

              <FormControl
                size='small'
                sx={{
                  minWidth: { xs: '100%', sm: '176px' },
                  width: { xs: '100%', sm: 'auto' },
                  display: 'none',
                }}
              >
                <Select
                  value='effective-rent'
                  disabled
                  sx={{
                    borderRadius: '0.5rem',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D1D5DB',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#39585C',
                    },
                  }}
                >
                  <MenuItem value='effective-rent' disabled>
                    Effective Rent (coming soon)
                  </MenuItem>
                  <MenuItem value='concessions' disabled>
                    Concessions % (coming soon)
                  </MenuItem>
                  <MenuItem value='occupancy' disabled>
                    Occupancy % (coming soon)
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size='small'
                sx={{
                  minWidth: { xs: '100%', sm: '144px' },
                  width: { xs: '100%', sm: 'auto' },
                  display: 'none',
                }}
              >
                <Select
                  value={rentBasis}
                  onChange={e => setRentBasis(e.target.value)}
                  sx={{
                    borderRadius: '0.5rem',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D1D5DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#39585C',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#39585C',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#39585C',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          '&:hover': {
                            backgroundColor: '#F0F4F5',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#F0F4F5',
                            color: '#39585C',
                            '&:hover': {
                              backgroundColor: '#F0F4F5',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value='asking'>Asking</MenuItem>
                  <MenuItem value='effective' disabled>
                    Effective
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size='small'
                sx={{
                  minWidth: { xs: '100%', sm: '144px' },
                  width: { xs: '100%', sm: 'auto' },
                  display: 'none',
                }}
              >
                <Select
                  value={timeWindow}
                  onChange={e => setTimeWindow(e.target.value)}
                  sx={{
                    borderRadius: '0.5rem',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D1D5DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#39585C',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#39585C',
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#39585C',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          '&:hover': {
                            backgroundColor: '#F0F4F5',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#F0F4F5',
                            color: '#39585C',
                            '&:hover': {
                              backgroundColor: '#F0F4F5',
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value='30'>Last 30 days</MenuItem>
                  <MenuItem value='60'>Last 60 days</MenuItem>
                  <MenuItem value='90'>Last 90 days</MenuItem>
                  <MenuItem value='180'>Last 180 days</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant='outlined'
                size='small'
                startIcon={<Sparkles size={16} />}
                onClick={() => setShowPresetsMenu(true)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '0.5rem',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  paddingX: activePresets.length > 0 ? '0.75rem' : '1rem',
                  borderColor: '#D1D5DB',
                  color: '#111827',
                  '&:hover': {
                    borderColor: '#39585C',
                    backgroundColor: '#F0F4F5',
                  },
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Typography>Presets</Typography>
                  {activePresets.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: '#39585C',
                        color: '#FFFFFF',
                        borderRadius: '10px',
                        minWidth: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingX: '0.375rem',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                      }}
                    >
                      {activePresets.length}
                    </Box>
                  )}
                </Box>
              </Button>
              {showClearAllButton && (
                <Button
                  variant='text'
                  size='small'
                  onClick={handleClearAll}
                  sx={{
                    textTransform: 'none',
                    color: '#6B7280',
                    '&:hover': {
                      color: '#39585C',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>

            {activePresets.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  paddingTop: '0.5rem',
                  borderTop: '1px solid #E5E7EB',
                }}
              >
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Active Presets:
                </Typography>
                <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {activePresets.map(presetId => (
                    <Chip
                      key={presetId}
                      label={presetId}
                      size='small'
                      onDelete={() =>
                        setActivePresets(
                          activePresets.filter(id => id !== presetId),
                        )
                      }
                      sx={{
                        fontSize: '0.75rem',
                        '& .MuiChip-deleteIcon': {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
            height: '100%',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', lg: '40%' },
              height: { xs: '300px', lg: '100%' },
              backgroundColor: '#F3F4F6',
              borderRight: { xs: 'none', lg: '1px solid #E5E7EB' },
              borderBottom: { xs: '1px solid #E5E7EB', lg: 'none' },
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0,
              display: { xs: showMapOnMobile ? 'block' : 'none', lg: 'block' },
            }}
          >
            <div
              ref={mapRef}
              style={{
                width: '100%',
                height: '100%',
              }}
            />

            <Card
              sx={{
                position: 'absolute',
                top: { xs: '0.5rem', md: '1rem' },
                left: { xs: '0.5rem', md: '1rem' },
                padding: { xs: '0.5rem', md: '0.75rem' },
                backgroundColor: '#FFFFFF',
                borderRadius: '0.5rem',
                boxShadow: 2,
                zIndex: 10,
                maxWidth: { xs: 'calc(100% - 1rem)', md: 'auto' },
                overflow: 'visible',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.625rem', md: '0.75rem' },
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem',
                }}
              >
                Area Selection
              </Typography>
              <GeometryTools
                onGeometryChange={setGeometries}
                radius={searchRadius}
                onRadiusChange={handleRadiusChange}
              />
            </Card>

            <Card
              sx={{
                position: 'absolute',
                bottom: { xs: '0.5rem', md: '1rem' },
                left: { xs: '0.5rem', md: '1rem' },
                padding: { xs: '0.5rem', md: '0.75rem' },
                backgroundColor: '#FFFFFF',
                borderRadius: '0.5rem',
                boxShadow: 2,
                minWidth: { xs: '140px', md: '180px' },
                zIndex: 10,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.625rem', md: '0.75rem' },
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.5rem',
                }}
              >
                Map Layers
              </Typography>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: '#39585C',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    Properties
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: '#F97316',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    Pipeline
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: '#EAB308',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    Heatmap
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              backgroundColor: '#FFFFFF',
              padding: { xs: '1rem', md: '1.5rem' },
              height: '100%',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                maxWidth: '1280px',
                marginX: 'auto',
                marginBottom: { xs: '1rem', md: '1.5rem' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
              >
                <Button
                  size='small'
                  startIcon={<Plus size={16} />}
                  onClick={() => setShowCardLibrary(true)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '0.5rem',
                    color: '#39585C',
                    '&:hover': {
                      backgroundColor: '#F0F4F5',
                    },
                  }}
                >
                  Add Card
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                maxWidth: '1280px',
                marginX: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '1rem', md: '1.5rem' },
                paddingBottom: { xs: '2rem', md: '3rem' },
              }}
            >
              {searchError && (
                <Alert
                  severity='error'
                  onClose={() => setSearchError(null)}
                  sx={{ marginBottom: '1rem' }}
                >
                  {searchError}
                </Alert>
              )}

              {searchResults && (
                <Card
                  sx={{
                    padding: '1.5rem',
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <Box sx={{ marginBottom: '1rem' }}>
                    <Typography
                      sx={{
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: '0.25rem',
                      }}
                    >
                      Found Buildings
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#6B7280',
                        marginBottom: '0.5rem',
                        maxWidth: '100%',
                      }}
                    >
                      {useExtentFiltering ? (
                        <>Found {filteredBuildings.length} building{filteredBuildings.length !== 1 ? 's' : ''} visible on map</>
                      ) : (
                        <>Found {searchResults.count} building{searchResults.count !== 1 ? 's' : ''} within{' '}
                        {searchResults.radius || searchRadius} miles of{' '}
                        {searchResults.searchAddress}</>
                      )}
                    </Typography>
                    {!useExtentFiltering && filteredBuildings.length !== searchResults.count && (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          marginBottom: '0.75rem',
                        }}
                      >
                        • Showing {filteredBuildings.length} after filters
                      </Typography>
                    )}
                    {useExtentFiltering && (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          marginBottom: '0.75rem',
                        }}
                      >
                        • {originalSearchResults?.buildings?.length || 0} total in search area
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid #E5E7EB',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                          fontWeight: 500,
                        }}
                      >
                        Radius:
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.375rem',
                          backgroundColor: radiusFilterActive
                            ? '#E8F0F1'
                            : '#F3F4F6',
                          border: `1px solid ${
                            radiusFilterActive ? '#4FA490' : '#E5E7EB'
                          }`,
                        }}
                      >
                        <Box
                          sx={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: radiusFilterActive
                              ? '#4FA490'
                              : '#9CA3AF',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: radiusFilterActive ? '#4FA490' : '#6B7280',
                          }}
                        >
                          {radiusFilterActive ? 'ON' : 'OFF'}
                        </Typography>
                      </Box>
                      <Button
                        size='small'
                        variant={radiusFilterActive ? 'outlined' : 'contained'}
                        onClick={() => setRadiusFilterActive(true)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          paddingX: '0.75rem',
                          ...(radiusFilterActive && {
                            borderColor: '#39585C',
                            color: '#39585C',
                            '&:hover': {
                              borderColor: '#2A3F42',
                              backgroundColor: '#F0F4F5',
                            },
                          }),
                          ...(!radiusFilterActive && {
                            backgroundColor: '#39585C',
                            '&:hover': { backgroundColor: '#2A3F42' },
                          }),
                        }}
                      >
                        Apply Radius
                      </Button>
                      <Button
                        size='small'
                        variant={radiusFilterActive ? 'contained' : 'outlined'}
                        onClick={() => {
                          setRadiusFilterActive(false);
                          setUseExtentFiltering(false);
                          setExtentFilteredBuildings(null);
                        }}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          paddingX: '0.75rem',
                          ...(radiusFilterActive && {
                            backgroundColor: '#39585C',
                            '&:hover': { backgroundColor: '#2A3F42' },
                          }),
                          ...(!radiusFilterActive && {
                            borderColor: '#D1D5DB',
                            color: '#6B7280',
                            '&:hover': {
                              borderColor: '#9CA3AF',
                              backgroundColor: '#F9FAFB',
                            },
                          }),
                        }}
                      >
                        Remove Radius
                      </Button>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: '1rem',
                      maxHeight: '400px',
                      overflowY: 'auto',
                    }}
                  >
                    {filteredBuildings.map(building => (
                      <Card
                        key={building.id}
                        sx={{
                          padding: '1rem',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          '&:hover': {
                            boxShadow: 2,
                            borderColor: '#39585C',
                          },
                        }}
                      >
                        {/* Top line: building name when distinct, otherwise address */}
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#111827',
                            marginBottom: '0.5rem',
                          }}
                        >
                          {building.name &&
                          building.name !== building.address
                            ? building.name
                            : building.address}
                        </Typography>
                        {/* Address below: only when we showed a distinct name above */}
                        {building.name &&
                          building.name !== building.address && (
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#6B7280',
                                marginBottom: '0.75rem',
                              }}
                            >
                              {building.address}
                            </Typography>
                          )}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              sx={{ fontSize: '0.75rem', color: '#6B7280' }}
                            >
                              Total units:
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                color: '#111827',
                              }}
                            >
                              {building.totalUnits ?? 0}
                            </Typography>
                          </Box>
                          {building.distance && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography
                                sx={{ fontSize: '0.75rem', color: '#6B7280' }}
                              >
                                Distance:
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  color: '#111827',
                                }}
                              >
                                {building.distance} mi
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Card>
              )}

              {sortedCards.map(activeCard => (
                <Box
                  key={activeCard.id}
                  draggable
                  onDragStart={() => handleDragStart(activeCard.id)}
                  onDragOver={e => handleDragOver(e, activeCard.id)}
                  onDragEnd={handleDragEnd}
                  sx={{
                    opacity: draggedCard === activeCard.id ? 0.5 : 1,
                    cursor: 'move',
                  }}
                >
                  <DataCard
                    id={activeCard.id}
                    pinned={activeCard.pinned}
                    data={filteredBuildings}
                    onToggleVisibility={() =>
                      toggleCardVisibility(activeCard.id)
                    }
                    onTogglePin={() => toggleCardPin(activeCard.id)}
                    onDuplicate={() => duplicateCard(activeCard.id)}
                    onRemove={() => removeCard(activeCard.id)}
                    sectionDataByKey={sectionDataState}
                    sectionTitleByKey={sectionTitleByKey}
                    defaultTemplateId={DEFAULT_TEMPLATE_ID}
                    demographicsData={demographicsData}
                    addressInfo={demographicsAddressInfo}
                    demographicsWebmapID={demographicsWebmapID}
                    arcgisToken={arcgisToken}
                    incomeData={incomeData}
                    incomeWebmapID={incomeWebmapID}
                    incomeAddressInfo={incomeAddressInfo}
                    expansionData={expansionData}
                    expansionWebmapID={expansionWebmapID}
                    expansionAddressInfo={expansionAddressInfo}
                    expansionSectionLoading={expansionSectionLoading}
                    crimeData={crimeData}
                    crimeWebmapID={crimeWebmapID}
                    crimeAddressInfo={crimeAddressInfo}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={saveMenuAnchor}
        open={Boolean(saveMenuAnchor)}
        onClose={() => setSaveMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setSaveDialogType('template');
            setShowSaveDialog(true);
            setSaveMenuAnchor(null);
          }}
        >
          <Save size={16} style={{ marginRight: '0.5rem' }} />
          Save as Template
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSaveDialogType('view');
            setShowSaveDialog(true);
            setSaveMenuAnchor(null);
          }}
        >
          <MapPin size={16} style={{ marginRight: '0.5rem' }} />
          Save View (with location)
        </MenuItem>
      </Menu>

      {showSaveDialog && (
        <Dialog
          open={showSaveDialog}
          onClose={() => setShowSaveDialog(false)}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '0.75rem',
            },
          }}
        >
          <DialogTitle>
            Save {saveDialogType === 'template' ? 'Template' : 'View'}
          </DialogTitle>
          <DialogContent>
            {saveError && (
              <Alert
                severity='error'
                onClose={() => setSaveError(null)}
                sx={{ mt: 1 }}
              >
                {saveError}
              </Alert>
            )}
            <TextField
              fullWidth
              label='Name'
              placeholder={
                saveDialogType === 'template'
                  ? 'e.g., Large MF Analysis'
                  : 'e.g., Dallas Uptown - 3mi'
              }
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              sx={{ marginTop: '1rem' }}
            />
            <TextField
              fullWidth
              label='Description'
              placeholder='Optional description...'
              multiline
              rows={3}
              value={saveDescription}
              onChange={e => setSaveDescription(e.target.value)}
              sx={{ marginTop: '1rem' }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowSaveDialog(false)}
              sx={{ textTransform: 'none' }}
              disabled={saveLoading}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={saveLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#39585C',
                '&:hover': {
                  backgroundColor: '#2d4649',
                },
              }}
            >
              {saveLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={showCardLibrary}
        onClose={() => {
          setShowCardLibrary(false);
          setCardLibrarySearch('');
          setSelectedCategory('all');
        }}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '0.75rem',
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: '1rem',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                Card Library
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  marginTop: '0.25rem',
                }}
              >
                Add cards to your analysis
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                setShowCardLibrary(false);
                setCardLibrarySearch('');
                setSelectedCategory('all');
              }}
              sx={{ padding: '0.5rem' }}
            >
              <X size={20} />
            </IconButton>
          </Box>

          <Box
            sx={{
              borderTop: '1px solid #E5E7EB',
              paddingTop: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <Search
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6B7280',
                  width: '16px',
                  height: '16px',
                }}
              />
              <TextField
                placeholder='Search cards...'
                value={cardLibrarySearch}
                onChange={e => setCardLibrarySearch(e.target.value)}
                fullWidth
                size='small'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    paddingLeft: '2.5rem',
                    borderRadius: '0.75rem',
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {CATEGORIES.map(category => (
                <Chip
                  key={category.id}
                  label={category.name}
                  onClick={() => setSelectedCategory(category.id)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor:
                      selectedCategory === category.id
                        ? '#39585C'
                        : 'transparent',
                    color:
                      selectedCategory === category.id ? '#FFFFFF' : '#374151',
                    border:
                      selectedCategory === category.id
                        ? '1px solid #39585C'
                        : '1px solid #D1D5DB',
                    '&:hover': {
                      backgroundColor:
                        selectedCategory === category.id
                          ? '#2d4649'
                          : '#F3F4F6',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            overflowY: 'auto',
            padding: '1.5rem',
            paddingTop: '0',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
              },
              gap: '1rem',
            }}
          >
            {availableCards.filter(card => {
              const matchesSearch =
                card.name
                  .toLowerCase()
                  .includes(cardLibrarySearch.toLowerCase()) ||
                card.description
                  .toLowerCase()
                  .includes(cardLibrarySearch.toLowerCase());
              const matchesCategory =
                selectedCategory === 'all' ||
                card.category === selectedCategory;
              return matchesSearch && matchesCategory;
            }).map(card => {
              const IconComponent = card.icon;
              const isActive = activeCards.some(ac => ac.id === card.id);
              return (
                <Card
                  key={card.id}
                  sx={{
                    padding: '1rem',
                    borderRadius: '0.75rem',
                    border: isActive
                      ? '1px solid #39585C'
                      : '1px solid #E5E7EB',
                    backgroundColor: isActive ? '#F0F4F5' : '#FFFFFF',
                    '&:hover': {
                      boxShadow: 2,
                      borderColor: '#39585C',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: '0.75rem',
                    }}
                  >
                    <Box
                      sx={{
                        color: '#39585C',
                        marginTop: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <IconComponent size={20} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'start',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                          marginBottom: '0.75rem',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: '#111827',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {card.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6B7280',
                              lineHeight: 1.4,
                            }}
                          >
                            {card.description}
                          </Typography>
                        </Box>
                        <Button
                          size='small'
                          variant={isActive ? 'outlined' : 'contained'}
                          onClick={() => !isActive && addCard(card.id)}
                          disabled={isActive}
                          sx={{
                            textTransform: 'none',
                            flexShrink: 0,
                            minWidth: 'auto',
                            paddingX: isActive ? '0.75rem' : '0.5rem',
                            borderRadius: '0.5rem',
                            backgroundColor: isActive
                              ? 'transparent'
                              : '#39585C',
                            borderColor: isActive ? '#39585C' : 'transparent',
                            color: isActive ? '#39585C' : '#FFFFFF',
                            '&:hover': {
                              backgroundColor: isActive ? '#F0F4F5' : '#2d4649',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'transparent',
                              borderColor: '#39585C',
                              color: '#39585C',
                            },
                          }}
                        >
                          {isActive ? (
                            'Added'
                          ) : (
                            <Plus size={16} style={{ margin: 0 }} />
                          )}
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          marginTop: '0.75rem',
                          padding: '0.5rem',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '0.5rem',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#6B7280',
                          }}
                        >
                          {card.preview}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>
          {availableCards.filter(card => {
            const matchesSearch =
              card.name
                .toLowerCase()
                .includes(cardLibrarySearch.toLowerCase()) ||
              card.description
                .toLowerCase()
                .includes(cardLibrarySearch.toLowerCase());
            const matchesCategory =
              selectedCategory === 'all' || card.category === selectedCategory;
            return matchesSearch && matchesCategory;
          }).length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                paddingY: '3rem',
              }}
            >
              <Typography sx={{ color: '#6B7280' }}>
                No cards found matching your search
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{ padding: '1rem 1.5rem', borderTop: '1px solid #E5E7EB' }}
        >
          <Button
            onClick={() => {
              setShowCardLibrary(false);
              setCardLibrarySearch('');
              setSelectedCategory('all');
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {showTemplatesManager && (
        <TemplatesManager
          onClose={() => setShowTemplatesManager(false)}
          onApplyTemplate={handleApplyTemplate}
          onOpenView={handleOpenView}
        />
      )}

      {showPresetsMenu && (
        <PresetsMenu
          onClose={() => setShowPresetsMenu(false)}
          onApplyPreset={handleApplyPreset}
          activePresets={activePresets}
        />
      )}

      {showPropertyFilters && (
        <PropertyFiltersDialog
          onClose={() => setShowPropertyFilters(false)}
          onApply={handleApplyPropertyFilters}
          onClear={handleClearPropertyFilters}
          initialFilters={appliedPropertyFilters}
        />
      )}

      {showExportShare && (
        <ExportShareMenu
          onClose={() => setShowExportShare(false)}
          discoverState={getCurrentDiscoverState()}
        />
      )}
    </Box>
  );
};

export default Discover;
